import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function authHeaders() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('adminToken')}` };
}

const ACTION_COLORS = {
  login_success: { bg: '#1a4731', color: '#3fb950', label: 'Login ✓' },
  login_failed: { bg: '#3d1a1a', color: '#f85149', label: 'Login ✗' },
  login_otp_sent: { bg: '#1f2d4a', color: '#58a6ff', label: 'OTP Sent' },
  otp_failed: { bg: '#3d1a1a', color: '#f85149', label: 'OTP ✗' },
  account_locked: { bg: '#3d2a1a', color: '#e3b341', label: 'Locked' },
  login_blocked: { bg: '#3d2a1a', color: '#e3b341', label: 'Blocked' },
  token_refreshed: { bg: '#1a2d1a', color: '#56d364', label: 'Refresh' },
  prompt_extraction_blocked: { bg: '#3d1a2d', color: '#db61a2', label: '⚠ Extraction' },
};

function ActionBadge({ action }) {
  const cfg = ACTION_COLORS[action] || { bg: '#21262d', color: '#8b949e', label: action };
  return <span style={{ background: cfg.bg, color: cfg.color, padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{cfg.label}</span>;
}

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(dateStr).toLocaleDateString();
}

function StatusPill({ active }) {
  return <span style={{ background: active ? '#1a4731' : '#3d1a1a', color: active ? '#3fb950' : '#f85149', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{active ? '● Active' : '● Disabled'}</span>;
}

function useBulkSelect(rows, getId) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const lastIndex = useRef(null);
  const menuRef = useRef(null);
  const clear = useCallback(() => {
    setSelectedIds(new Set()); setAllMatching(false); setMenuOpen(false); lastIndex.current = null;
  }, []);
  useEffect(() => { clear(); }, [rows, clear]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = event => { if (!menuRef.current?.contains(event.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);
  const ids = rows.map(getId);
  const selectedOnPage = ids.filter(id => selectedIds.has(id)).length;
  const allOnPage = ids.length > 0 && selectedOnPage === ids.length;
  const toggleRow = (id, index, shiftKey) => {
    if (allMatching) {
      const pageIds = new Set(ids); pageIds.delete(id);
      setAllMatching(false); setSelectedIds(pageIds); lastIndex.current = index;
      return;
    }
    // Read the anchor now: the state updater runs later, after lastIndex is overwritten below.
    const anchor = lastIndex.current;
    setSelectedIds(current => {
      const next = new Set(current);
      const selecting = !next.has(id);
      if (shiftKey && anchor !== null) {
        const [start, end] = [anchor, index].sort((a, b) => a - b);
        ids.slice(start, end + 1).forEach(rangeId => selecting ? next.add(rangeId) : next.delete(rangeId));
      } else if (selecting) next.add(id); else next.delete(id);
      return next;
    });
    lastIndex.current = index;
  };
  const toggleAll = () => {
    if (allMatching || allOnPage) clear();
    else { setSelectedIds(new Set(ids)); setAllMatching(false); }
  };
  const selectRows = predicate => {
    setSelectedIds(new Set(rows.filter(predicate).map(getId))); setAllMatching(false); setMenuOpen(false);
  };
  return { selectedIds, allMatching, menuOpen, setMenuOpen, menuRef, selectedOnPage, allOnPage, clear, toggleRow, toggleAll, selectRows, selectAllMatching: () => { setAllMatching(true); setSelectedIds(new Set()); setMenuOpen(false); } };
}

function MasterCheckbox({ checked, indeterminate, onChange }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = indeterminate; }, [indeterminate]);
  return <input ref={ref} type="checkbox" checked={checked} onChange={onChange} aria-label="Select rows" />;
}

function BulkSelectControl({ selection, options }) {
  return <div ref={selection.menuRef} style={s.bulkSelectControl}>
    <MasterCheckbox checked={selection.allMatching || selection.allOnPage} indeterminate={!selection.allMatching && selection.selectedOnPage > 0 && !selection.allOnPage} onChange={selection.toggleAll} />
    <button type="button" style={s.bulkMenuButton} aria-label="Selection options" onClick={() => selection.setMenuOpen(!selection.menuOpen)}>▾</button>
    {selection.menuOpen && <div style={s.bulkMenu}>
      <button type="button" style={s.bulkMenuItem} onClick={() => selection.selectRows(() => true)}>All</button>
      <button type="button" style={s.bulkMenuItem} onClick={selection.clear}>None</button>
      {options.map(([label, predicate]) => <button type="button" key={label} style={s.bulkMenuItem} onClick={() => selection.selectRows(predicate)}>{label}</button>)}
    </div>}
  </div>;
}

export default function AdminDashboard() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '', recoveryEmail: '' });
  const [resumeUsers, setResumeUsers] = useState([]);
  const [newResumeUser, setNewResumeUser] = useState({ username: '', fullName: '', password: '' });
  const [lavanUsers, setLavanUsers] = useState([]);
  const [newLavanUser, setNewLavanUser] = useState({ username: '', email: '', password: '', recoveryEmail: '' });
  const [resetPwdId, setResetPwdId] = useState(null);
  const [resetPwdVal, setResetPwdVal] = useState('');
  const [logs, setLogs] = useState([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditFilter, setAuditFilter] = useState({ action: '', username: '', success: '' });
  const [auditAppliedFilter, setAuditAppliedFilter] = useState({ action: '', username: '', success: '' });
  const [auditLoading, setAuditLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  const [sessionsPage, setSessionsPage] = useState(1);
  const [sessionsFilter, setSessionsFilter] = useState({ candidateName: '', status: '' });
  const [sessionsAppliedFilter, setSessionsAppliedFilter] = useState({ candidateName: '', status: '' });
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const sessionsBulk = useBulkSelect(sessions, session => session.chatCode);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const auditBulk = useBulkSelect(logs, log => log._id);
  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({ email: '', recoveryEmail: '', currentPassword: '', password: '' });
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [promptSaving, setPromptSaving] = useState(false);
  const [showNewPrompt, setShowNewPrompt] = useState(false);
  const [newPromptKey, setNewPromptKey] = useState('');
  const [newPromptType, setNewPromptType] = useState('persona');
  const [newPromptContent, setNewPromptContent] = useState('');
  const [promptCreating, setPromptCreating] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('adminToken')) { navigate('/admin'); return; }
    loadUsers(); loadProfile();
  }, []);

  useEffect(() => {
    if (tab === 'audit') loadAuditLogs(1);
    if (tab === 'sessions') loadSessions(1);
    if (tab === 'resumeUsers') loadResumeUsers();
    if (tab === 'lavanUsers') loadLavanUsers();
    if (tab === 'prompts') loadPrompts();
    sessionsBulk.clear(); auditBulk.clear();
    setResetPwdId(null); setResetPwdVal('');
  }, [tab]);

  function flash(type, text) {
    if (type === 'ok') { setMsg(text); setError(''); } else { setError(text); setMsg(''); }
    setTimeout(() => { setMsg(''); setError(''); }, 4000);
  }

  async function apiFetch(path, opts = {}) {
    const res = await fetch(`${API}${path}`, { headers: authHeaders(), ...opts });
    const data = await res.json();
    if (res.status === 401) { logout(); return null; }
    if (!res.ok) throw new Error(data.message || 'Request failed');
    return data;
  }

  async function loadUsers() {
    try { const d = await apiFetch('/api/admin/users'); if (d) setUsers(d.users); } catch (e) { flash('err', e.message); }
  }
  async function createUser(e) {
    e.preventDefault();
    try { await apiFetch('/api/admin/users', { method: 'POST', body: JSON.stringify(newUser) }); flash('ok', '✅ User created'); setNewUser({ username: '', email: '', password: '', recoveryEmail: '' }); loadUsers(); } catch (e) { flash('err', e.message); }
  }
  async function toggleUser(id, isActive) {
    try { await apiFetch(`/api/admin/users/${id}/toggle`, { method: 'PATCH' }); setUsers(users.map(u => u._id === id ? { ...u, isActive: !isActive } : u)); } catch (e) { flash('err', e.message); }
  }
  async function deleteUser(id, username) {
    if (!window.confirm(`Delete user "${username}"? This cannot be undone.`)) return;
    try { await apiFetch(`/api/admin/users/${id}`, { method: 'DELETE' }); setUsers(users.filter(u => u._id !== id)); flash('ok', '✅ User deleted'); } catch (e) { flash('err', e.message); }
  }

  async function loadResumeUsers() {
    try { const d = await apiFetch('/api/admin/resume-users'); if (d) setResumeUsers(d.users); } catch (e) { flash('err', e.message); }
  }
  async function createResumeUser(e) {
    e.preventDefault();
    try { await apiFetch('/api/admin/resume-users', { method: 'POST', body: JSON.stringify(newResumeUser) }); flash('ok', '✅ Resume user created'); setNewResumeUser({ username: '', fullName: '', password: '' }); loadResumeUsers(); } catch (e) { flash('err', e.message); }
  }
  async function toggleResumeUser(id, currentStatus) {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try { await apiFetch(`/api/admin/resume-users/${id}`, { method: 'PUT', body: JSON.stringify({ status: newStatus }) }); setResumeUsers(resumeUsers.map(u => u.id === id ? { ...u, status: newStatus } : u)); } catch (e) { flash('err', e.message); }
  }
  async function deleteResumeUser(id, username) {
    if (!window.confirm(`Delete resume user "${username}"? This cannot be undone.`)) return;
    try { await apiFetch(`/api/admin/resume-users/${id}`, { method: 'DELETE' }); setResumeUsers(resumeUsers.filter(u => u.id !== id)); flash('ok', '✅ Resume user deleted'); } catch (e) { flash('err', e.message); }
  }
  async function saveResumePassword(id) {
    if (!resetPwdVal || resetPwdVal.length < 6) { flash('err', 'Password must be at least 6 characters'); return; }
    try { await apiFetch(`/api/admin/resume-users/${id}`, { method: 'PUT', body: JSON.stringify({ password: resetPwdVal }) }); flash('ok', '✅ Password reset'); setResetPwdId(null); setResetPwdVal(''); } catch (e) { flash('err', e.message); }
  }

  async function loadLavanUsers() {
    try { const d = await apiFetch('/api/admin/lavan-users'); if (d) setLavanUsers(d.users); } catch (e) { flash('err', e.message); }
  }
  async function createLavanUser(e) {
    e.preventDefault();
    try { await apiFetch('/api/admin/lavan-users', { method: 'POST', body: JSON.stringify(newLavanUser) }); flash('ok', '✅ Lavan user created'); setNewLavanUser({ username: '', email: '', password: '', recoveryEmail: '' }); loadLavanUsers(); } catch (e) { flash('err', e.message); }
  }
  async function toggleLavanUser(id, currentStatus) {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try { await apiFetch(`/api/admin/lavan-users/${id}/toggle`, { method: 'PATCH' }); setLavanUsers(lavanUsers.map(u => u.id === id ? { ...u, status: newStatus } : u)); } catch (e) { flash('err', e.message); }
  }
  async function deleteLavanUser(id, username) {
    if (!window.confirm(`Delete Lavan user "${username}"? This cannot be undone.`)) return;
    try { await apiFetch(`/api/admin/lavan-users/${id}`, { method: 'DELETE' }); setLavanUsers(lavanUsers.filter(u => u.id !== id)); flash('ok', '✅ Lavan user deleted'); } catch (e) { flash('err', e.message); }
  }
  async function saveLavanPassword(id) {
    if (!resetPwdVal || resetPwdVal.length < 8) { flash('err', 'Password must be at least 8 characters'); return; }
    try { await apiFetch(`/api/admin/lavan-users/${id}`, { method: 'PUT', body: JSON.stringify({ password: resetPwdVal }) }); flash('ok', '✅ Password reset'); setResetPwdId(null); setResetPwdVal(''); } catch (e) { flash('err', e.message); }
  }

  const loadSessions = async (page = 1, filter = sessionsFilter) => {
    sessionsBulk.clear();
    setSessionsLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 30 });
      if (filter.candidateName) params.set('candidateName', filter.candidateName);
      if (filter.status) params.set('status', filter.status);
      const res = await fetch(`${API}/api/admin/sessions?${params}`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) { setSessions(data.sessions); setSessionsTotal(data.pagination.total); setSessionsPage(page); setSessionsAppliedFilter({ ...filter }); } else flash('err', data.message);
    } catch { flash('err', 'Failed to load sessions'); }
    setSessionsLoading(false);
  };
  const deleteAdminSession = async (chatCode) => {
    if (!window.confirm(`Delete session ${chatCode} and all its messages?`)) return;
    try {
      const res = await fetch(`${API}/api/admin/sessions/${chatCode}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (res.ok) { flash('ok', data.message); loadSessions(sessionsPage); } else flash('err', data.message);
    } catch { flash('err', 'Delete failed'); }
  };

  async function bulkDeleteSessions() {
    const count = sessionsBulk.allMatching ? sessionsTotal : sessionsBulk.selectedIds.size;
    if (!window.confirm(`Delete ${count} session(s) and all their messages? This cannot be undone.`)) return;
    const body = sessionsBulk.allMatching ? { allMatching: true, filter: sessionsAppliedFilter } : { chatCodes: [...sessionsBulk.selectedIds] };
    if (bulkDeleting) return;
    setBulkDeleting(true);
    try {
      const data = await apiFetch('/api/admin/sessions/bulk-delete', { method: 'POST', body: JSON.stringify(body) });
      if (!data) return;
      flash('ok', data.message);
      const nextPage = sessionsBulk.allMatching ? 1 : (sessionsBulk.selectedIds.size === sessions.length ? Math.max(1, sessionsPage - 1) : sessionsPage);
      loadSessions(nextPage, sessionsAppliedFilter);
    } catch (e) { flash('err', e.message); } finally { setBulkDeleting(false); }
  }

  const loadAuditLogs = useCallback(async (page = 1, filter = auditFilter) => {
    auditBulk.clear();
    setAuditLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (filter.action) params.set('action', filter.action);
      if (filter.username) params.set('username', filter.username);
      if (filter.success !== '') params.set('success', filter.success);
      const d = await apiFetch(`/api/audit-logs?${params}`);
      if (d) { setLogs(d.logs); setAuditTotal(d.pagination.total); setAuditPage(page); setAuditAppliedFilter({ ...filter }); }
    } catch (e) { flash('err', e.message); } finally { setAuditLoading(false); }
  }, [auditFilter]);

  async function bulkDeleteAuditLogs() {
    const count = auditBulk.allMatching ? auditTotal : auditBulk.selectedIds.size;
    const label = count === 1 ? 'entry' : 'entries';
    if (!window.confirm(`Delete ${count} audit log ${label}? This cannot be undone.`)) return;
    const body = auditBulk.allMatching ? { allMatching: true, filter: auditAppliedFilter } : { ids: [...auditBulk.selectedIds] };
    if (bulkDeleting) return;
    setBulkDeleting(true);
    try {
      const data = await apiFetch('/api/audit-logs/bulk-delete', { method: 'POST', body: JSON.stringify(body) });
      if (!data) return;
      flash('ok', data.message);
      const nextPage = auditBulk.allMatching ? 1 : (auditBulk.selectedIds.size === logs.length ? Math.max(1, auditPage - 1) : auditPage);
      loadAuditLogs(nextPage, auditAppliedFilter);
    } catch (e) { flash('err', e.message); } finally { setBulkDeleting(false); }
  }

  async function loadProfile() {
    try { const d = await apiFetch('/api/admin/profile'); if (d) { setProfile(d.user); setProfileForm({ email: d.user.email, recoveryEmail: d.user.recoveryEmail || '', currentPassword: '', password: '' }); } } catch (e) { flash('err', e.message); }
  }
  async function updateProfile(e) {
    e.preventDefault();
    try { await apiFetch('/api/admin/profile', { method: 'PUT', body: JSON.stringify(profileForm) }); flash('ok', '✅ Profile updated'); loadProfile(); } catch (e) { flash('err', e.message); }
  }

  async function loadPrompts() {
    try {
      const d = await apiFetch('/api/admin/prompts');
      if (d) { setPrompts(d.prompts || []); setSelectedPrompt(null); setEditContent(''); }
    } catch (e) { flash('err', e.message); }
  }
  async function savePrompt() {
    if (!selectedPrompt) return;
    if (!editContent.trim() || editContent.trim().length < 50) { flash('err', 'Prompt must be at least 50 characters'); return; }
    setPromptSaving(true);
    try {
      const result = await apiFetch(`/api/admin/prompts/${encodeURIComponent(selectedPrompt.key)}`, { method: 'PUT', body: JSON.stringify({ content: editContent }) });
      const serverPrompt = result?.prompt || { content: editContent };
      flash('ok', `✅ Prompt "${selectedPrompt.key}" saved & cache reloaded`);
      setPrompts(prev => prev.map(p => p.key === selectedPrompt.key ? { ...p, ...serverPrompt } : p));
      setSelectedPrompt(prev => ({ ...prev, ...serverPrompt }));
      setEditContent(serverPrompt.content);
    } catch (e) { flash('err', e.message); }
    setPromptSaving(false);
  }
  async function createPrompt() {
    if (!newPromptKey.trim()) { flash('err', 'Key name is required'); return; }
    if (!newPromptContent.trim() || newPromptContent.trim().length < 10) { flash('err', 'Content must be at least 10 characters'); return; }
    setPromptCreating(true);
    try {
      const d = await apiFetch('/api/admin/prompts', { method: 'POST', body: JSON.stringify({ key: newPromptKey, type: newPromptType, content: newPromptContent }) });
      flash('ok', `✅ Prompt "${d.prompt.key}" created`);
      const updatedPrompts = [...prompts, d.prompt].sort((a, b) => a.key.localeCompare(b.key));
      setPrompts(updatedPrompts);
      setShowNewPrompt(false);
      setNewPromptKey(''); setNewPromptType('persona'); setNewPromptContent('');
      setSelectedPrompt(d.prompt); setEditContent(d.prompt.content);
    } catch (e) { flash('err', e.message); }
    setPromptCreating(false);
  }
  async function deletePrompt(key) {
    if (!window.confirm(`Delete prompt "${key}"? This cannot be undone.`)) return;
    try {
      await apiFetch(`/api/admin/prompts/${encodeURIComponent(key)}`, { method: 'DELETE' });
      flash('ok', `✅ Prompt "${key}" deleted`);
      setPrompts(prompts.filter(p => p.key !== key));
      if (selectedPrompt?.key === key) { setSelectedPrompt(null); setEditContent(''); }
    } catch (e) { flash('err', e.message); }
  }

  function logout() { localStorage.removeItem('adminToken'); localStorage.removeItem('adminUser'); navigate('/admin'); }

  const TABS = [
    ['users', '👥 Users'],
    ['sessions', '💬 Sessions'],
    ['audit', '🛡 Audit Log'],
    ['profile', '⚙️ Profile'],
    ['resumeUsers', '📄 Resume Users'],
    ['lavanUsers', '⚡ Lavan Users'],
    ['prompts', '📝 Prompts'],
  ];

  return (
    <div style={{ ...s.page, flexDirection: isMobile ? 'column' : 'row' }}>
      <div style={isMobile ? { ...s.sidebar, width: '100%', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', padding: '10px 12px', gap: 8 } : s.sidebar}>
        <div style={s.logo}><img src={`${process.env.PUBLIC_URL}/logo.png`} alt="" onError={e => { e.currentTarget.style.display = 'none'; }} style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover' }} />Lavan AI</div>
        <span style={s.adminBadge}>ADMIN</span>
        <nav style={isMobile ? { flex: 1, marginTop: 0, display: 'flex', overflowX: 'auto', gap: 6 } : { flex: 1, marginTop: 24 }}>
          {TABS.map(([key, label]) => (
            <button key={key} style={{ ...s.navBtn, ...(tab === key ? s.navActive : {}), ...(isMobile ? { width: 'auto', whiteSpace: 'nowrap', flexShrink: 0, marginBottom: 0 } : {}) }} onClick={() => setTab(key)}>{label}</button>
          ))}
        </nav>
        <button style={s.logoutBtn} onClick={logout}>🚪 Logout</button>
      </div>
      <div style={isMobile ? { ...s.main, padding: '18px 14px' } : s.main}>
        {msg && <div style={s.successBanner}>{msg}</div>}
        {error && <div style={s.errorBanner}>{error}</div>}

        {tab === 'users' && (<>
          <div style={s.pageHeader}><h2 style={s.heading}>User Management</h2><span style={s.badge}>{users.length} / 3 users</span></div>
          <div style={s.card}>
            <h3 style={s.cardTitle}>Add New User</h3>
            <form onSubmit={createUser} style={s.form}>
              <div style={s.formRow}>
                <input style={s.input} placeholder="Username" value={newUser.username} onChange={e => setNewUser({ ...newUser, username: e.target.value })} required />
                <input style={s.input} placeholder="Email" type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} required />
              </div>
              <div style={s.formRow}>
                <input style={s.input} placeholder="Password" type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} required />
                <input style={s.input} placeholder="Recovery Email (optional)" type="email" value={newUser.recoveryEmail} onChange={e => setNewUser({ ...newUser, recoveryEmail: e.target.value })} />
              </div>
              <button style={{ ...s.btnPrimary, opacity: users.length >= 3 ? 0.5 : 1 }} type="submit" disabled={users.length >= 3}>{users.length >= 3 ? '⛔ Max 3 users reached' : '+ Create User'}</button>
            </form>
          </div>
          <div style={s.card}>
            <h3 style={s.cardTitle}>All Users</h3>
            {users.length === 0 ? <p style={s.empty}>No users yet.</p> :
              <table style={s.table}>
                <thead><tr>{['Username','Email','Status','Created','Actions'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
                <tbody>{users.map(u => (
                  <tr key={u._id} style={s.tr}>
                    <td style={s.td}><strong>{u.username}</strong></td>
                    <td style={s.td}>{u.email}</td>
                    <td style={s.td}><StatusPill active={u.isActive} /></td>
                    <td style={s.td}>{new Date(u.createdAt).toLocaleDateString()}</td>
                    <td style={s.td}>
                      <button style={s.btnToggle} onClick={() => toggleUser(u._id, u.isActive)}>{u.isActive ? 'Disable' : 'Enable'}</button>
                      <button style={s.btnDelete} onClick={() => deleteUser(u._id, u.username)}>Delete</button>
                    </td>
                  </tr>
                ))}</tbody>
              </table>}
          </div>
        </>)}

        {tab === 'resumeUsers' && (<>
          <div style={s.pageHeader}><h2 style={s.heading}>Resume Builder Users</h2><span style={s.badge}>{resumeUsers.length} users</span></div>
          <div style={s.card}>
            <h3 style={s.cardTitle}>Add New Resume User</h3>
            <form onSubmit={createResumeUser} style={s.form}>
              <div style={s.formRow}>
                <input style={s.input} placeholder="Username" value={newResumeUser.username} onChange={e => setNewResumeUser({ ...newResumeUser, username: e.target.value })} required />
                <input style={s.input} placeholder="Full Name (optional)" value={newResumeUser.fullName} onChange={e => setNewResumeUser({ ...newResumeUser, fullName: e.target.value })} />
              </div>
              <div style={s.formRow}>
                <input style={s.input} placeholder="Password (min 6 characters)" type="password" value={newResumeUser.password} onChange={e => setNewResumeUser({ ...newResumeUser, password: e.target.value })} required />
                <div style={{ flex: 1 }} />
              </div>
              <button style={s.btnPrimary} type="submit">+ Create Resume User</button>
            </form>
          </div>
          <div style={s.card}>
            <h3 style={s.cardTitle}>All Resume Users</h3>
            {resumeUsers.length === 0 ? <p style={s.empty}>No resume users yet.</p> :
              <table style={s.table}>
                <thead><tr>{['Username','Full Name','Status','Created','Last Login','Actions'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
                <tbody>{resumeUsers.map(u => (
                  <tr key={u.id} style={s.tr}>
                    <td style={s.td}><strong>{u.username}</strong></td>
                    <td style={{ ...s.td, color: '#8b949e' }}>{u.fullName || '—'}</td>
                    <td style={s.td}><StatusPill active={u.status === 'active'} /></td>
                    <td style={{ ...s.td, fontSize: 13, color: '#8b949e' }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                    <td style={{ ...s.td, fontSize: 13, color: '#8b949e' }}>{timeAgo(u.lastLogin)}</td>
                    <td style={s.td}>
                      {resetPwdId === u.id ? (
                        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                          <input style={{ ...s.input, width: 140, padding: '4px 10px', fontSize: 12 }} type="password" placeholder="New password" value={resetPwdVal} onChange={e => setResetPwdVal(e.target.value)} autoFocus />
                          <button style={{ ...s.btnPrimary, padding: '4px 10px', fontSize: 12 }} onClick={() => saveResumePassword(u.id)}>Save</button>
                          <button style={{ ...s.btnToggle, marginRight: 0 }} onClick={() => { setResetPwdId(null); setResetPwdVal(''); }}>✕</button>
                        </span>
                      ) : (<>
                        <button style={{ ...s.btnToggle, marginRight: 6 }} onClick={() => { setResetPwdId(u.id); setResetPwdVal(''); }}>Reset Pwd</button>
                        <button style={s.btnToggle} onClick={() => toggleResumeUser(u.id, u.status)}>{u.status === 'active' ? 'Disable' : 'Enable'}</button>
                        <button style={s.btnDelete} onClick={() => deleteResumeUser(u.id, u.username)}>Delete</button>
                      </>)}
                    </td>
                  </tr>
                ))}</tbody>
              </table>}
          </div>
        </>)}

        {tab === 'lavanUsers' && (<>
          <div style={s.pageHeader}><h2 style={s.heading}>Lavan Users</h2><span style={s.badge}>{lavanUsers.length} users</span></div>
          <div style={s.card}>
            <h3 style={s.cardTitle}>Add New Lavan User</h3>
            <form onSubmit={createLavanUser} style={s.form}>
              <div style={s.formRow}>
                <input style={s.input} placeholder="Username" value={newLavanUser.username} onChange={e => setNewLavanUser({ ...newLavanUser, username: e.target.value })} required />
                <input style={s.input} placeholder="Email" type="email" value={newLavanUser.email} onChange={e => setNewLavanUser({ ...newLavanUser, email: e.target.value })} required />
              </div>
              <div style={s.formRow}>
                <input style={s.input} placeholder="Password (min 8 characters)" type="password" value={newLavanUser.password} onChange={e => setNewLavanUser({ ...newLavanUser, password: e.target.value })} required />
                <input style={s.input} placeholder="Recovery Email (optional)" type="email" value={newLavanUser.recoveryEmail} onChange={e => setNewLavanUser({ ...newLavanUser, recoveryEmail: e.target.value })} />
              </div>
              <button style={s.btnPrimary} type="submit">+ Create Lavan User</button>
            </form>
          </div>
          <div style={s.card}>
            <h3 style={s.cardTitle}>All Lavan Users</h3>
            {lavanUsers.length === 0 ? <p style={s.empty}>No Lavan users yet.</p> :
              <table style={s.table}>
                <thead><tr>{['Username','Email','Status','Created','Last Login','Actions'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
                <tbody>{lavanUsers.map(u => (
                  <tr key={u.id} style={s.tr}>
                    <td style={s.td}><strong>{u.username}</strong></td>
                    <td style={{ ...s.td, color: '#8b949e' }}>{u.email}</td>
                    <td style={s.td}><StatusPill active={u.status === 'active'} /></td>
                    <td style={{ ...s.td, fontSize: 13, color: '#8b949e' }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                    <td style={{ ...s.td, fontSize: 13, color: '#8b949e' }}>{timeAgo(u.lastLoginAt)}</td>
                    <td style={s.td}>
                      {resetPwdId === u.id ? (
                        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                          <input style={{ ...s.input, width: 140, padding: '4px 10px', fontSize: 12 }} type="password" placeholder="New password" value={resetPwdVal} onChange={e => setResetPwdVal(e.target.value)} autoFocus />
                          <button style={{ ...s.btnPrimary, padding: '4px 10px', fontSize: 12 }} onClick={() => saveLavanPassword(u.id)}>Save</button>
                          <button style={{ ...s.btnToggle, marginRight: 0 }} onClick={() => { setResetPwdId(null); setResetPwdVal(''); }}>✕</button>
                        </span>
                      ) : (<>
                        <button style={{ ...s.btnToggle, marginRight: 6 }} onClick={() => { setResetPwdId(u.id); setResetPwdVal(''); }}>Reset Pwd</button>
                        <button style={s.btnToggle} onClick={() => toggleLavanUser(u.id, u.status)}>{u.status === 'active' ? 'Disable' : 'Enable'}</button>
                        <button style={s.btnDelete} onClick={() => deleteLavanUser(u.id, u.username)}>Delete</button>
                      </>)}
                    </td>
                  </tr>
                ))}</tbody>
              </table>}
          </div>
        </>)}

        {tab === 'sessions' && (<>
          <div style={s.pageHeader}><h2 style={s.heading}>Sessions</h2><span style={s.badge}>{sessionsTotal} total</span></div>
          <div style={{ ...s.card, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
            <div style={{ flex: 1, minWidth: 160 }}><p style={s.filterLabel}>Candidate Name</p><input style={s.input} placeholder="Search name…" value={sessionsFilter.candidateName} onChange={e => setSessionsFilter(f => ({ ...f, candidateName: e.target.value }))} /></div>
            <div style={{ minWidth: 130 }}><p style={s.filterLabel}>Status</p><select style={s.select} value={sessionsFilter.status} onChange={e => setSessionsFilter(f => ({ ...f, status: e.target.value }))}><option value="">All</option><option value="active">Active</option><option value="completed">Completed</option></select></div>
            <button style={s.btnPrimary} onClick={() => loadSessions(1)}>Search</button>
            <button style={s.btnToggle} onClick={() => { const filter = { candidateName: '', status: '' }; setSessionsFilter(filter); loadSessions(1, filter); }}>Clear</button>
          </div>
          <div style={s.card}>
            {sessionsLoading ? <p style={s.empty}>Loading…</p> : sessions.length === 0 ? <p style={s.empty}>No sessions found.</p> : (<>
              {(sessionsBulk.selectedIds.size > 0 || sessionsBulk.allMatching) && <div style={s.bulkActionBar}><strong>{sessionsBulk.allMatching ? sessionsTotal : sessionsBulk.selectedIds.size} selected</strong><span>·</span><button style={{ ...s.btnDelete, opacity: bulkDeleting ? 0.5 : 1 }} disabled={bulkDeleting} onClick={bulkDeleteSessions}>{bulkDeleting ? 'Deleting…' : '🗑 Delete'}</button><button style={s.btnToggle} onClick={sessionsBulk.clear}>Clear</button></div>}
              {sessionsBulk.allOnPage && !sessionsBulk.allMatching && sessionsTotal > sessions.length && <div style={s.selectAllBanner}>All {sessions.length} on this page are selected. <button style={s.textButton} onClick={sessionsBulk.selectAllMatching}>Select all {sessionsTotal} matching this search</button></div>}
              {sessionsBulk.allMatching && <div style={s.selectAllBanner}>All {sessionsTotal} matching this search are selected. <button style={s.textButton} onClick={sessionsBulk.clear}>Clear selection</button></div>}
              <table style={s.table}>
                <thead><tr><th style={{ ...s.th, ...s.checkboxCell }}><BulkSelectControl selection={sessionsBulk} options={[["Active", row => row.status === 'active'], ["Completed", row => row.status === 'completed']]} /></th><th style={s.th}>Chat Code</th><th style={s.th}>Candidate</th><th style={s.th}>Role</th><th style={s.th}>Client</th><th style={s.th}>Mode</th><th style={s.th}>Status</th><th style={s.th}>Created</th><th style={s.th}></th></tr></thead>
                <tbody>{sessions.map((s2, index) => (
                  <tr key={s2.chatCode} style={{ ...s.tr, ...(sessionsBulk.allMatching || sessionsBulk.selectedIds.has(s2.chatCode) ? s.selectedRow : {}) }}>
                    <td style={{ ...s.td, ...s.checkboxCell }}><input type="checkbox" checked={sessionsBulk.allMatching || sessionsBulk.selectedIds.has(s2.chatCode)} onClick={e => e.stopPropagation()} onChange={e => sessionsBulk.toggleRow(s2.chatCode, index, e.nativeEvent.shiftKey)} aria-label={`Select session ${s2.chatCode}`} /></td>
                    <td style={{ ...s.td, fontFamily: "'JetBrains Mono', monospace", color: '#0866FF', fontWeight: 600 }}>{s2.chatCode}</td>
                    <td style={s.td}>{s2.candidateName || <span style={{ color: '#484f58' }}>—</span>}</td>
                    <td style={{ ...s.td, fontSize: 13, color: '#8b949e' }}>{s2.interviewingRole || '—'}</td>
                    <td style={{ ...s.td, fontSize: 13, color: '#8b949e' }}>{s2.interviewingClient || '—'}</td>
                    <td style={s.td}><span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: s2.mode === 'general' ? '#E8F1FF' : '#DCFCE7', color: s2.mode === 'general' ? '#0866FF' : '#166534' }}>{s2.mode}</span></td>
                    <td style={s.td}><StatusPill active={s2.status === 'active'} /></td>
                    <td style={{ ...s.td, fontSize: 12, color: '#8b949e' }}>{timeAgo(s2.createdAt)}</td>
                    <td style={s.td}><button style={s.btnDelete} onClick={() => deleteAdminSession(s2.chatCode)}>Delete</button></td>
                  </tr>
                ))}</tbody>
              </table>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, fontSize: 13, color: '#8b949e' }}>
                <span>Showing {sessions.length} of {sessionsTotal}</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={{ ...s.btnToggle, opacity: sessionsPage <= 1 ? 0.4 : 1 }} disabled={sessionsPage <= 1} onClick={() => loadSessions(sessionsPage - 1)}>← Prev</button>
                  <span style={{ padding: '5px 12px', background: '#F1F5F9', borderRadius: 6 }}>Page {sessionsPage}</span>
                  <button style={{ ...s.btnToggle, opacity: sessions.length < 30 ? 0.4 : 1 }} disabled={sessions.length < 30} onClick={() => loadSessions(sessionsPage + 1)}>Next →</button>
                </div>
              </div>
            </>)}
          </div>
        </>)}

        {tab === 'audit' && (<>
          <div style={s.pageHeader}><h2 style={s.heading}>Audit Log</h2><span style={s.badge}>{auditTotal} events</span></div>
          <div style={{ ...s.card, paddingBottom: 16 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: 1, minWidth: 140 }}><div style={s.filterLabel}>Action</div>
                <select style={s.select} value={auditFilter.action} onChange={e => setAuditFilter({ ...auditFilter, action: e.target.value })}>
                  <option value="">All actions</option><option value="login_success">Login Success</option><option value="login_failed">Login Failed</option>
                  <option value="login_otp_sent">OTP Sent</option><option value="otp_failed">OTP Failed</option><option value="account_locked">Account Locked</option>
                  <option value="login_blocked">Login Blocked</option><option value="token_refreshed">Token Refreshed</option><option value="prompt_extraction_blocked">Prompt Extraction</option>
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 140 }}><div style={s.filterLabel}>Username</div><input style={s.input} placeholder="Filter by username" value={auditFilter.username} onChange={e => setAuditFilter({ ...auditFilter, username: e.target.value })} /></div>
              <div style={{ flex: 1, minWidth: 120 }}><div style={s.filterLabel}>Result</div>
                <select style={s.select} value={auditFilter.success} onChange={e => setAuditFilter({ ...auditFilter, success: e.target.value })}><option value="">All</option><option value="true">Success</option><option value="false">Failed</option></select>
              </div>
              <button style={{ ...s.btnPrimary, padding: '10px 20px', minWidth: 80 }} onClick={() => loadAuditLogs(1)}>Search</button>
              <button style={{ ...s.btnToggle, padding: '10px 16px' }} onClick={() => { const filter = { action: '', username: '', success: '' }; setAuditFilter(filter); loadAuditLogs(1, filter); }}>Clear</button>
            </div>
          </div>
          <div style={s.card}>
            {auditLoading ? <p style={s.empty}>Loading…</p> : logs.length === 0 ? <p style={s.empty}>No log entries found.</p> : (<>
              {(auditBulk.selectedIds.size > 0 || auditBulk.allMatching) && <div style={s.bulkActionBar}><strong>{auditBulk.allMatching ? auditTotal : auditBulk.selectedIds.size} selected</strong><span>·</span><button style={{ ...s.btnDelete, opacity: bulkDeleting ? 0.5 : 1 }} disabled={bulkDeleting} onClick={bulkDeleteAuditLogs}>{bulkDeleting ? 'Deleting…' : '🗑 Delete'}</button><button style={s.btnToggle} onClick={auditBulk.clear}>Clear</button></div>}
              {auditBulk.allOnPage && !auditBulk.allMatching && auditTotal > logs.length && <div style={s.selectAllBanner}>All {logs.length} on this page are selected. <button style={s.textButton} onClick={auditBulk.selectAllMatching}>Select all {auditTotal} matching this search</button></div>}
              {auditBulk.allMatching && <div style={s.selectAllBanner}>All {auditTotal} matching this search are selected. <button style={s.textButton} onClick={auditBulk.clear}>Clear selection</button></div>}
              <table style={s.table}>
                <thead><tr><th style={{ ...s.th, ...s.checkboxCell }}><BulkSelectControl selection={auditBulk} options={[["Success", row => row.success], ["Failed", row => !row.success]]} /></th>{['Time','Action','User','IP','Result','Detail'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
                <tbody>{logs.map((log, index) => (
                  <tr key={log._id} style={{ ...s.tr, ...(auditBulk.allMatching || auditBulk.selectedIds.has(log._id) ? s.selectedRow : {}) }}>
                    <td style={{ ...s.td, ...s.checkboxCell }}><input type="checkbox" checked={auditBulk.allMatching || auditBulk.selectedIds.has(log._id)} onClick={e => e.stopPropagation()} onChange={e => auditBulk.toggleRow(log._id, index, e.nativeEvent.shiftKey)} aria-label="Select audit entry" /></td>
                    <td style={{ ...s.td, whiteSpace: 'nowrap', color: '#8b949e', fontSize: 12 }}>{timeAgo(log.createdAt)}</td>
                    <td style={s.td}><ActionBadge action={log.action} /></td>
                    <td style={s.td}>{log.username || <span style={{ color: '#484f58' }}>—</span>}</td>
                    <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 12, color: '#8b949e' }}>{log.ip || '—'}</td>
                    <td style={s.td}><span style={{ color: log.success ? '#3fb950' : '#f85149', fontSize: 12, fontWeight: 600 }}>{log.success ? '✓' : '✗'}</span></td>
                    <td style={{ ...s.td, color: '#8b949e', fontSize: 12, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.reason || (log.metadata?.attemptsLeft !== undefined ? `${log.metadata.attemptsLeft} attempts left` : '—')}</td>
                  </tr>
                ))}</tbody>
              </table>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, fontSize: 13, color: '#8b949e' }}>
                <span>Showing {logs.length} of {auditTotal} events</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={{ ...s.btnToggle, opacity: auditPage <= 1 ? 0.4 : 1 }} disabled={auditPage <= 1} onClick={() => loadAuditLogs(auditPage - 1)}>← Prev</button>
                  <span style={{ padding: '5px 12px', background: '#F1F5F9', borderRadius: 6 }}>Page {auditPage}</span>
                  <button style={{ ...s.btnToggle, opacity: logs.length < 50 ? 0.4 : 1 }} disabled={logs.length < 50} onClick={() => loadAuditLogs(auditPage + 1)}>Next →</button>
                </div>
              </div>
            </>)}
          </div>
        </>)}

        {tab === 'profile' && profile && (<>
          <h2 style={s.heading}>My Profile</h2>
          <div style={s.card}>
            <h3 style={s.cardTitle}>Update Admin Details</h3>
            <form onSubmit={updateProfile} style={{ ...s.form, maxWidth: 460 }}>
              <label style={s.label}>Email</label>
              <input style={s.input} type="email" value={profileForm.email} onChange={e => setProfileForm({ ...profileForm, email: e.target.value })} required />
              <label style={s.label}>Recovery Email</label>
              <input style={s.input} type="email" placeholder="backup@email.com" value={profileForm.recoveryEmail} onChange={e => setProfileForm({ ...profileForm, recoveryEmail: e.target.value })} />
              <label style={s.label}>Current Password <span style={{ color: '#8b949e', fontWeight: 400 }}>(required to change password)</span></label>
              <input style={s.input} type="password" placeholder="Current password" value={profileForm.currentPassword} onChange={e => setProfileForm({ ...profileForm, currentPassword: e.target.value })} />
              <label style={s.label}>New Password <span style={{ color: '#8b949e', fontWeight: 400 }}>(leave blank to keep current)</span></label>
              <input style={s.input} type="password" placeholder="New password" value={profileForm.password} onChange={e => setProfileForm({ ...profileForm, password: e.target.value })} />
              <button style={s.btnPrimary} type="submit">Save Changes</button>
            </form>
          </div>
        </>)}

        {tab === 'prompts' && (<>
          <div style={s.pageHeader}>
            <h2 style={s.heading}>Prompt Editor</h2>
            <span style={s.badge}>{prompts.length} prompts</span>
            <button style={{ ...s.btnPrimary, padding: '8px 16px', marginLeft: 'auto', fontSize: 13 }} onClick={() => { setShowNewPrompt(true); setSelectedPrompt(null); setEditContent(''); }}>+ New Role</button>
          </div>

          {showNewPrompt && (
            <div style={{ ...s.card, marginBottom: 20, border: '1px solid #86EFAC' }}>
              <h3 style={{ ...s.cardTitle, color: '#16A34A' }}>Create New Prompt / Role</h3>
              <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <p style={s.filterLabel}>Key Name (e.g. react-developer)</p>
                  <input style={s.input} placeholder="e.g. react-developer" value={newPromptKey} onChange={e => setNewPromptKey(e.target.value)} />
                </div>
                <div style={{ minWidth: 140 }}>
                  <p style={s.filterLabel}>Type</p>
                  <select style={s.select} value={newPromptType} onChange={e => setNewPromptType(e.target.value)}>
                    <option value="persona">🎭 Persona</option>
                    <option value="base">🧠 Base</option>
                  </select>
                </div>
              </div>
              <p style={s.filterLabel}>Prompt Content</p>
              <textarea
                style={{ width: '100%', minHeight: 200, padding: '12px 14px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, color: '#1F2937', fontSize: 13, lineHeight: 1.6, resize: 'vertical', outline: 'none', fontFamily: "'JetBrains Mono', monospace", boxSizing: 'border-box' }}
                placeholder="Enter the prompt content for this role..."
                value={newPromptContent}
                onChange={e => setNewPromptContent(e.target.value)}
                spellCheck={false}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button style={{ ...s.btnPrimary, padding: '10px 24px', opacity: promptCreating ? 0.6 : 1 }} onClick={createPrompt} disabled={promptCreating}>{promptCreating ? 'Creating…' : '✅ Create Prompt'}</button>
                <button style={s.btnToggle} onClick={() => { setShowNewPrompt(false); setNewPromptKey(''); setNewPromptContent(''); }}>Cancel</button>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
            <div style={{ width: 270, flexShrink: 0 }}>
              <div style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '10px 16px', borderBottom: '1px solid #E8EBF2', fontSize: 11, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Select a Prompt to Edit
                </div>
                <div style={{ maxHeight: 560, overflowY: 'auto' }}>
                  {prompts.length === 0 ? <p style={{ ...s.empty, padding: 16 }}>Loading…</p> : prompts.map(p => (
                    <div key={p.key} style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #F1F4F9', background: selectedPrompt?.key === p.key ? '#E8F1FF' : 'none' }}>
                      <button
                        onClick={() => { setSelectedPrompt(p); setEditContent(p.content); setShowNewPrompt(false); }}
                        style={{ flex: 1, textAlign: 'left', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', color: selectedPrompt?.key === p.key ? '#0866FF' : '#1F2937' }}
                      >
                        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{p.key}</div>
                        <div style={{ fontSize: 11, color: selectedPrompt?.key === p.key ? '#0866FF' : '#6B7280' }}>
                          {p.type === 'base' ? '🧠 Base' : '🎭 Persona'} · {(p.content || '').length.toLocaleString()} chars
                        </div>
                      </button>
                      {p.key !== 'SYSTEM_BASE' && (
                        <button onClick={() => deletePrompt(p.key)} style={{ padding: '4px 8px', margin: '0 6px', background: 'none', border: 'none', color: '#6e4040', cursor: 'pointer', fontSize: 14, borderRadius: 4 }} title="Delete">🗑</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ flex: 1 }}>
              {!selectedPrompt ? (
                <div style={{ ...s.card, color: '#8b949e', fontSize: 14, textAlign: 'center', padding: 48 }}>
                  ← Select a prompt on the left to edit it
                </div>
              ) : (
                <div style={s.card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 16, color: '#1F2937' }}>{selectedPrompt.key}</h3>
                      <span style={{ fontSize: 12, color: '#6B7280' }}>
                        {selectedPrompt.type === 'base' ? '🧠 Base prompt' : '🎭 Persona prompt'} · Last saved: {selectedPrompt.updatedAt ? new Date(selectedPrompt.updatedAt).toLocaleString() : 'not yet saved'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button style={{ ...s.btnToggle, marginRight: 0 }} onClick={() => setEditContent(selectedPrompt.content)}>↺ Reset</button>
                      <button style={{ ...s.btnPrimary, padding: '8px 20px', opacity: promptSaving ? 0.6 : 1 }} onClick={savePrompt} disabled={promptSaving}>{promptSaving ? 'Saving…' : '💾 Save & Reload'}</button>
                    </div>
                  </div>
                  <textarea
                    style={{ width: '100%', minHeight: 460, padding: '12px 14px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, color: '#1F2937', fontSize: 13, lineHeight: 1.6, resize: 'vertical', outline: 'none', fontFamily: "'JetBrains Mono', monospace", boxSizing: 'border-box' }}
                    value={editContent}
                    onChange={e => setEditContent(e.target.value)}
                    spellCheck={false}
                  />
                  <div style={{ marginTop: 8, fontSize: 12, color: '#8b949e' }}>
                    {editContent.length.toLocaleString()} characters · {editContent.split('\n').length} lines
                    {editContent !== selectedPrompt.content && <span style={{ color: '#e3b341', marginLeft: 10 }}>● Unsaved changes</span>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>)}

      </div>
    </div>
  );
}

const GRAD = 'linear-gradient(100deg,#3B3CC4 0%,#7C2BD0 30%,#C81E73 55%,#E51E50 72%,#F26A2C 100%)';
const s = {
  page: { display: 'flex', minHeight: '100vh', background: '#F7F9FC', color: '#1F2937', fontFamily: "'Hanken Grotesk', sans-serif" },
  sidebar: { width: 220, background: '#fff', borderRight: '1px solid #E8EBF2', padding: '24px 16px', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  logo: { fontSize: 17, fontWeight: 800, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 },
  adminBadge: { fontSize: 10, color: '#fff', background: GRAD, padding: '2px 8px', borderRadius: 4, alignSelf: 'flex-start', letterSpacing: 1, fontWeight: 700 },
  navBtn: { display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', background: 'none', border: 'none', color: '#6B7280', fontSize: 14, cursor: 'pointer', borderRadius: 8, marginBottom: 4, fontWeight: 600 },
  navActive: { background: '#E8F1FF', color: '#0866FF' },
  logoutBtn: { padding: '10px 12px', background: 'none', border: '1px solid #E5E7EB', color: '#6B7280', borderRadius: 8, cursor: 'pointer', fontSize: 13 },
  main: { flex: 1, padding: '32px 40px', overflowY: 'auto' },
  pageHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 },
  heading: { fontSize: 22, margin: 0, color: '#1F2937', fontWeight: 800 },
  badge: { fontSize: 12, background: '#E8F1FF', color: '#0866FF', padding: '3px 12px', borderRadius: 12, fontWeight: 600 },
  card: { background: '#fff', border: '1px solid #E8EBF2', borderRadius: 14, padding: 24, marginBottom: 24, overflowX: 'auto', boxShadow: '0 1px 3px rgba(15,23,42,0.04)' },
  cardTitle: { fontSize: 13, color: '#6B7280', marginBottom: 16, marginTop: 0, textTransform: 'uppercase', letterSpacing: 0.5 },
  form: { display: 'flex', flexDirection: 'column', gap: 12 },
  formRow: { display: 'flex', gap: 12 },
  label: { fontSize: 13, color: '#6B7280', fontWeight: 500 },
  filterLabel: { fontSize: 11, color: '#6B7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 },
  input: { flex: 1, padding: '10px 14px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, color: '#1F2937', fontSize: 14, outline: 'none', minWidth: 0, width: '100%', boxSizing: 'border-box' },
  select: { width: '100%', padding: '10px 14px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, color: '#1F2937', fontSize: 14, outline: 'none' },
  btnPrimary: { padding: '11px', background: GRAD, border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 },
  btnToggle: { marginRight: 8, padding: '5px 12px', background: '#F1F5F9', border: '1px solid #E5E7EB', color: '#374151', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  btnDelete: { padding: '5px 12px', background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#B91C1C', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  bulkActionBar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '9px 12px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 13, color: '#374151' },
  selectAllBanner: { marginBottom: 12, padding: '9px 12px', background: '#EEF4FF', border: '1px solid #BFD6FF', borderRadius: 8, color: '#1D4ED8', fontSize: 13 },
  textButton: { border: 'none', background: 'none', padding: 0, color: '#0866FF', cursor: 'pointer', fontWeight: 700, fontSize: 13, textDecoration: 'underline' },
  bulkSelectControl: { position: 'relative', display: 'flex', alignItems: 'center', gap: 3 },
  bulkMenuButton: { border: 'none', background: 'none', color: '#6B7280', padding: '0 2px', cursor: 'pointer', fontSize: 12, lineHeight: 1 },
  bulkMenu: { position: 'absolute', zIndex: 5, top: 22, left: 0, minWidth: 120, padding: 4, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 7, boxShadow: '0 4px 12px rgba(15,23,42,0.14)' },
  bulkMenuItem: { display: 'block', width: '100%', padding: '7px 9px', border: 'none', background: 'none', borderRadius: 4, textAlign: 'left', color: '#374151', cursor: 'pointer', fontSize: 12 },
  checkboxCell: { width: 34, paddingLeft: 8, paddingRight: 4 },
  selectedRow: { background: '#EEF4FF' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 520 },
  th: { textAlign: 'left', padding: '10px 12px', color: '#6B7280', fontSize: 11, borderBottom: '1px solid #E8EBF2', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  tr: { borderBottom: '1px solid #F1F4F9' },
  td: { padding: '12px 12px', fontSize: 14, color: '#1F2937', verticalAlign: 'middle' },
  empty: { color: '#9CA3AF', fontSize: 14, margin: 0 },
  successBanner: { background: '#DCFCE7', border: '1px solid #86EFAC', color: '#166534', padding: '10px 16px', borderRadius: 8, marginBottom: 20, fontSize: 14 },
  errorBanner: { background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#B91C1C', padding: '10px 16px', borderRadius: 8, marginBottom: 20, fontSize: 14 },
};
